import {
	Component,
	computed,
	effect,
	HostListener,
	inject,
	linkedSignal,
	OnDestroy,
	OnInit,
	Renderer2,
	signal,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { Toast } from '@openng/optimus-ui/toast'
import { TranslatePipe, TranslateService, TranslationObject } from '@ngx-translate/core'
import { NgClass } from '@angular/common'
import { HeaderComponent } from './shared/header/header'
import { LanguageService } from './core/language.service'
// The JSON files contain non-string Optimus locale values (numbers, arrays) that
// ngx-translate's strict `TranslationObject` type rejects; they are valid at runtime.
import translationsEN from '../../public/i18n/en.json'
import translationsAR from '../../public/i18n/ar.json'
import translationsFA from '../../public/i18n/fa.json'
import Keycloak from 'keycloak-js'
import { ApprovalWaitListRequestDto, ApprovalWaitListService, AppUserService } from 'api'
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular'
import { User } from './shared/state/user'
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental'
import { filter, interval, lastValueFrom, Subscription } from 'rxjs'
import { LayoutService } from './shared/ui/layout.service'
import { SidebarComponent } from './shared/sidebar/sidebar'
import { Optimus } from '@openng/optimus-ui/config'
import { Button, ButtonDirective } from '@openng/optimus-ui/button'
import { BrowserNotificationService } from './core/browser-notification-service'
import { HydrationStateService } from './core/hydration-state.service'
import { CacheService } from './shared/cache-service/cache.service'
import { Keys } from './shared/cache-service/keys'
import { CacheType } from './shared/cache-service/cache-type.enum'
import { toSignal } from '@angular/core/rxjs-interop'
import { MessageService, PrimeTemplate } from '@openng/optimus-ui/api'
import AcceptAgreementModal from './features/trading/profile/term-and-agreements/accept-agreement-modal/accept-agreement-modal'
import { explicitEffect } from 'ngxtension/explicit-effect'
import { SwUpdate, type UnrecoverableStateEvent, VersionReadyEvent } from '@angular/service-worker'
import { SwalService } from './shared/swal'
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog'
import { Dialog } from '@openng/optimus-ui/dialog'
import { FooterComponent } from './shared/footer/footer'
import GlobalTopBannerComponent from './shared/components/global-top-banner/global-top-banner'
import { BottomNavComponent } from './shared/bottom-nav/bottom-nav'

@Component({
	standalone: true,
	selector: 'app-root',
	imports: [
		RouterOutlet,
		Toast,
		HeaderComponent,
		NgClass,
		TranslatePipe,
		SidebarComponent,
		Button,
		AcceptAgreementModal,
		ConfirmDialog,
		Dialog,
		ButtonDirective,
		PrimeTemplate,
		FooterComponent,
		GlobalTopBannerComponent,
		BottomNavComponent,
	],
	templateUrl: './app.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './app.scss',
})
export class AppComponent implements OnInit, OnDestroy {
	// 🔥 متغیرهای مربوط به تایتل چشمک‌زن
	// SSR guard: `document` is not available on the server; resolve lazily.
	originalTitle = typeof document !== 'undefined' ? document.title : ''
	flashInterval: any

	title = 'bazargah'
	#translateService = inject(TranslateService)
	#cacheService = inject(CacheService)
	keycloakService = inject(Keycloak)
	messageService = inject(MessageService)
	// webSocketService = inject(WebSocketStomp)
	// ✅ یک متغیر برای مدیریت اشتراک وب‌سوکت
	notificationSubscription: Subscription | undefined
	browserNotification = inject(BrowserNotificationService)
	#appUserService = inject(AppUserService)
	protected layoutService = inject(LayoutService)
	#renderer = inject(Renderer2)
	#router = inject(Router)
	#user = inject(User)
	#keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL)
	/** گیت رندر همسان سرور/کلاینت برای بخش‌های وابسته به احراز هویت (SSR). */
	readonly hydration = inject(HydrationStateService)
	#optimus = inject(Optimus)
	#langChangeSubscription?: Subscription
	protected langService = inject(LanguageService)
	// #queryClient = inject(QueryClient)
	#swalService = inject(SwalService)
	#swUpdate = inject(SwUpdate) // ✅  تزریق سرویس SwUpdate
	#approvalWaitingList = inject(ApprovalWaitListService)

	keyCloakIsReady = computed(() => this.#keycloakSignal().type === KeycloakEventType.Ready)
	loggedOut = computed(() => this.#keycloakSignal().type === KeycloakEventType.AuthLogout)

	overlayMenuOpenSubscription: Subscription

	menuOutsideClickListener: any

	@ViewChild(SidebarComponent) appSidebar!: SidebarComponent

	@ViewChild(HeaderComponent) headerComponent!: HeaderComponent

	firstLogin = toSignal(this.#cacheService.getItem<boolean>(Keys.FirstLogin, CacheType.SESSION_STORAGE), {
		initialValue: false,
	})
	role =
		this.keycloakService.realmAccess?.roles.filter(realmRole =>
			['SUPER_ADMIN', 'CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USERS', 'TRAINEE_USER', 'TRAINER_USER', 'USER'].includes(
				realmRole
			)
		) || []
	roles = signal([
		{
			key: 'TRAINER_USER',
			title: 'مدرس',
			desc: 'برگزاری دوره‌های آموزشی',
			bg: 'bg-purple-600',
			icon: 'pi-graduation-cap',
		},
		{
			key: 'CLINIC_ADMIN',
			title: 'مدیر کلینیک',
			desc: 'مدیریت کلینیک',
			bg: 'bg-blue-500',
			icon: 'pi-building',
		},
		{
			key: 'SALON_ADMIN',
			title: 'مدیر سالن',
			desc: 'مدیریت سالن',
			bg: 'bg-blue-500',
			icon: 'pi-building',
		},
		{
			key: 'SELLER_USERS',
			title: 'فروشنده تجهیزات',
			desc: 'فروش تجهیزات زیبایی',
			bg: 'bg-orange-500',
			icon: 'pi-box',
		},
		{
			key: 'TRAINEE_USER',
			title: 'هنرجو/بیوتی‌آرتیست',
			desc: 'شرکت در دوره‌های آموزشی',
			bg: 'bg-green-500',
			icon: 'pi-book',
		},
		{
			key: 'SUPER_ADMIN',
			title: 'سوپر ادمین',
			desc: 'مدیریت سیستم',
			bg: 'bg-gray-500',
			icon: 'pi-user',
		},
	])

	syncAppUser = injectQuery(() => ({
		enabled: false,
		queryKey: ['sync-app'],
		queryFn: () => lastValueFrom(this.#appUserService.syncAppUser({ firstLogin: this.firstLogin() })),
	}))
	paddingInline = !this.keycloakService.authenticated ? '0' : null

	getAccount = injectQuery(() => ({
		enabled: this.keyCloakIsReady() && !!this.syncAppUser.data(),
		queryKey: ['get-account', this.syncAppUser.data()],
		queryFn: () => lastValueFrom(this.#appUserService.getAccount()),
		staleTime: 10 * 1000 * 60,
	}))

	selectRoleMutation = injectMutation(() => ({
		mutationFn: (data: any) => {
			console.log('mutationFn called')
			return lastValueFrom(this.#approvalWaitingList.createApprovalWaitList(data))
		},
		onSuccess: () => {
			this.dialogVisible.set(false)
			this.messageService.add({
				detail: 'عملیات با موفقیت انجام شده و پس از تایید تیم BEAUTY BOSS کاربری شما قابل استفاده خواهد بود.',
				key: 'tc',
				life: 10000,
				severity: 'success',
			})
			// this.spaceTypeGetPage.refetch()
		},
		onError: () => {
			this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error' })
		},
	}))

	hasUnsignedAgreements = computed(() => (this.getAccount.data()?.unsignedAgreements?.length || 0) > 0)
	isShahkarVerified = computed(() => this.getAccount.data()?.shahkarVerified ?? false)
	isUnassignedAgreementModalVisible = computed(() => (this.isShahkarVerified() ? this.hasUnsignedAgreements() : false))
	firstUnsignedAgreementId = computed(() =>
		this.hasUnsignedAgreements() ? this.getAccount.data()!.unsignedAgreements![0] : null
	)
	dialogVisible = signal(false)

	isLandingPage = signal(typeof window !== 'undefined' ? window.location.pathname === '/' : false)

	refCode = toSignal(this.#cacheService.getItem<string>(Keys.Ref, CacheType.LOCAL_STORAGE), { initialValue: '' })

	constructor() {
		console.log(this.#router.url)

		this.subscribeToUpdates()
		this.setupUpdatePolling()

		this.#router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			// SSR guard: NavigationEnd also fires during server render.
			const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
			const url = new URL(event.urlAfterRedirects, origin)
			const price = url.searchParams.get('price')
			const quantity = url.searchParams.get('quantity')

			if (price !== null && quantity !== null) {
				return
			}

			this.isLandingPage.set(event.urlAfterRedirects === '/')
			if (typeof window !== 'undefined') {
				window.scrollTo({ top: 0, behavior: 'smooth' })
			}
		})

		effect(() => {
			if (this.syncAppUser.isFetched()) {
				this.#cacheService.setItem(Keys.FirstLogin, false, CacheType.SESSION_STORAGE)
			}
		})

		effect(() => {
			if (this.getAccount.data()) {
				this.#user.setUser(this.getAccount.data()!)
			}
		})

		// effect(() => {
		// 	const isLegalAccountAndNotApproved =
		// 		this.getAccount.data()?.legalAccountStatus === AppUserGetAccountRecLegalAccountStatusEnum.PendingApproval ||
		// 		this.getAccount.data()?.legalAccountStatus === AppUserGetAccountRecLegalAccountStatusEnum.Rejected
		// 	if (
		// 		this.getAccount.isEnabled() &&
		// 		this.getAccount.isFetched() &&
		// 		(this.isShahkarVerified() === false || (this.isShahkarVerified() && isLegalAccountAndNotApproved))
		// 	) {
		// 		void this.#router.navigate(['/authorization'])
		// 		this.layoutService.layoutState.update(prev => ({
		// 			...prev,
		// 			overlayMenuActive: false,
		// 			staticMenuDesktopInactive: true,
		// 			staticMenuMobileActive: false,
		// 		}))
		// 	}
		// })

		effect(() => {
			if (this.loggedOut()) {
				this.layoutService.layoutState.update(prev => ({
					...prev,
					overlayMenuActive: false,
					staticMenuDesktopInactive: true,
					staticMenuMobileActive: false,
				}))
				if (typeof window !== 'undefined') {
					window.location.replace('/')
				}
			}
		})

		this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
			if (!this.menuOutsideClickListener) {
				this.menuOutsideClickListener = this.#renderer.listen('document', 'click', event => {
					if (this.isOutsideClicked(event)) {
						this.hideMenu()
					}
				})
			}

			if (this.layoutService.layoutState().staticMenuMobileActive) {
				this.blockBodyScroll()
			}
		})

		this.#router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			this.hideMenu()
		})

		explicitEffect([this.shouldShowIosGuide], ([shouldShowIosGuide]) => {
			if (shouldShowIosGuide) {
				setTimeout(() => this.shouldShowIosGuide.set(false), 7500)
			}
		})
	}

	scrollToTop(): void {
		if (typeof window === 'undefined') return
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	ngOnInit() {
		setTimeout(() => {
			console.log('rrrrrrrrrrrrrrrrole', this.role)
		}, 500)
		if (this.role.includes('USER')) {
			// setTimeout(() => {
			// 	this.dialogVisible.set(true)
			// }, 500)
		}

		// if (this.keycloakService.authenticated) {
		// 	this.browserNotification.requestPermission()
		//
		// 	// ✅  فراخوانی connect بسیار ساده‌تر شده است
		// 	this.webSocketService.connect()
		//
		// 	// ✅  اشتراک برای دریافت نوتیفیکیشن‌های شخصی کاربر
		// 	this.notificationSubscription = this.webSocketService
		// 		.subscribe<any>('/user/queue/notifications')
		// 		.subscribe(message => {
		// 			console.log('Global notification received:', message)
		//
		// 			// ✅ استفاده مجدد از منطق قبلی شما برای نمایش نوتیفیکیشن
		// 			this.messageService.add({
		// 				detail: message.title,
		// 				key: 'tc',
		// 				life: 6000,
		// 			})
		// 			if (message.title && (message.title === 'معامله فروش' || message.title === 'معامله خرید')) {
		// 				void this.#queryClient.invalidateQueries({ queryKey: ['my-stock-orders'] })
		// 				void this.#queryClient.invalidateQueries({ queryKey: ['my-money-wallet-balance'] })
		// 			}
		// 			if (message && message.title && message.message) {
		// 				this.browserNotification.showNotification(message.title, message.message)
		// 			}
		// 		})
		// }

		this.#translateService.addLangs(['fa', 'en', 'ar'])
		this.#translateService.setTranslation('en', translationsEN as unknown as TranslationObject)
		this.#translateService.setTranslation('ar', translationsAR as unknown as TranslationObject)
		this.#translateService.setTranslation('fa', translationsFA as unknown as TranslationObject)
		this.#translateService.setFallbackLang('fa')
		this.#translateService.use('fa')
		// Optimus locale must follow the UI language (confirm-dialog labels,
		// paginator aria, empty messages live under the "optimus" i18n key).
		this.#applyOptimusLocale()
		this.#langChangeSubscription = this.#translateService.onLangChange.subscribe(() => this.#applyOptimusLocale())

		if (this.keycloakService.authenticated) {
			void this.syncAppUser.refetch()
		}

		// window.addEventListener('beforeinstallprompt', e => {
		// 	console.log(e)
		// 	// Prevent Chrome 67 and earlier from automatically showing the prompt
		// 	// e.preventDefault()
		// 	// Stash the event so it can be triggered later.
		// 	this.deferredPrompt = e
		// 	this.showButton.set(true)
		// })

		// 🔥 اضافه کردن قابلیت تایتل چشمک‌زن و جذاب
		this.setupFlashingTitle()
		this.setupChunkLoadRecovery()
	}

	ngOnDestroy(): void {
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe()
		}
		this.#langChangeSubscription?.unsubscribe()
		// همچنین بهتر است اتصال وب‌سوکت را نیز قطع کنیم
		// this.webSocketService.disconnect()
	}

	#applyOptimusLocale(): void {
		this.#optimus.setTranslation(this.#translateService.instant('optimus'))
	}

	// 🔥 تابع جدید برای مدیریت تایتل
	setupFlashingTitle() {
		// SSR guard: ngOnInit also runs during server render.
		if (typeof document === 'undefined') return
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				// کاربر رفت! شروع چشمک‌زن
				let toggle = false
				this.flashInterval = setInterval(() => {
					document.title = toggle ? 'BeautyBoss - 🚀 جا نمونی!' : 'BeautyBoss - 👀 یه سر بزن'

					toggle = !toggle
				}, 1500) // هر 1.5 ثانیه عوض شود
			} else {
				// کاربر برگشت! توقف چشمک‌زن و بازگشت به حالت عادی
				clearInterval(this.flashInterval)
				document.title = this.originalTitle // یا مقدار 'bazargah'
			}
		})
	}

	subscribeToUpdates() {
		// ما فقط در حالت پروداکشن به آپدیت‌ها گوش می‌دهیم
		// و زمانی که سرویس آپدیت فعال باشد
		if (this.#swUpdate.isEnabled) {
			this.#swUpdate.versionUpdates
				.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
				.subscribe(() => {
					const message = this.#translateService.instant('updateAvailable')
					const title = this.#translateService.instant('updateTitle')
					const confirmButtonText = this.#translateService.instant('updateButton')

					// ❌  کد قبلی را حذف کنید
					// if (confirm(message)) {
					//     document.location.reload()
					// }

					// ✅  کد جدید SweetAlert را جایگزین کنید
					this.#swalService
						.fire({
							title: title,
							text: message,
							icon: 'info',
							showCancelButton: false,
							confirmButtonText: confirmButtonText,
							confirmButtonColor: '#3085d6', // می‌توانید رنگ دلخواه خود را تنظیم کنید
							allowOutsideClick: false,
							allowEscapeKey: false,
							allowEnterKey: false,
						})
						.then(result => {
							if (result.isConfirmed) {
								// صفحه را مجدداً بارگذاری می‌کنیم تا آپدیت اعمال شود
								document.location.reload()
							}
						})
				})

			// اگر سرویس‌ورکر به وضعیت غیرقابل‌بازیابی برسد (مثلاً چانک حذف‌شده بعد از دیپلوی)،
			// تنها راه ریکاوری یک رفرش کامل است.
			if (typeof document === 'undefined') return
			this.#swUpdate.unrecoverable.subscribe((event: UnrecoverableStateEvent) => {
				console.error('Service worker unrecoverable state:', event.reason)
				document.location.reload()
			})
		}
	}

	setupUpdatePolling() {
		if (this.#swUpdate.isEnabled) {
			// هر 1 ساعت (3600000 میلی‌ثانیه)
			// می‌توانید این زمان را کمتر کنید، مثلاً هر 15 دقیقه
			interval(60 * 60 * 1000).subscribe(() => {
				console.log('Checking for new app version...')
				this.#swUpdate.checkForUpdate()
			})
		}
	}

	/**
	 * ریکاوری از خطای بارگذاری چانک: بعد از دیپلوی، فایل‌های هش‌دار قبلی حذف می‌شوند و
	 * dynamic import شکست می‌خورد. حداکثر یک رفرش در هر ۳۰ ثانیه تا از حلقه رفرش جلوگیری شود.
	 */
	private setupChunkLoadRecovery(): void {
		if (typeof window === 'undefined') return
		window.addEventListener('unhandledrejection', event => {
			const reason: unknown = event.reason
			let message = ''
			if (reason instanceof Error) {
				message = reason.message
			} else if (typeof reason === 'string') {
				message = reason
			}

			const chunkFailurePattern =
				/dynamically imported module|Importing a module script failed|Loading chunk .* failed/i
			if (!chunkFailurePattern.test(message)) return

			try {
				const lastReloadAt = Number(sessionStorage.getItem('chunk-load-reload-at') ?? 0)
				if (Date.now() - lastReloadAt < 30_000) return
				sessionStorage.setItem('chunk-load-reload-at', String(Date.now()))
			} catch {
				// sessionStorage می‌تواند در حالت‌های محدود throw کند؛ بدون throttle رفرش نمی‌کنیم.
				return
			}

			document.location.reload()
		})
	}

	selectRole(role: any) {
		console.log('role.key', role.key)
		const data: ApprovalWaitListRequestDto = {
			requestRole: role.key,
		}
		this.selectRoleMutation.mutate({ approvalWaitListRequestDto: data })
	}

	// ..............UI................
	isOutsideClicked(event: MouseEvent) {
		const sidebarEl = document.querySelector('.layout-sidebar')
		const topbarEl = document.querySelector('.layout-menu-button')
		const eventTarget = event.target as Node

		return !(
			sidebarEl?.isSameNode(eventTarget) ||
			sidebarEl?.contains(eventTarget) ||
			topbarEl?.isSameNode(eventTarget) ||
			topbarEl?.contains(eventTarget)
		)
	}

	hideMenu() {
		this.layoutService.layoutState.update(prev => ({
			...prev,
			overlayMenuActive: false,
			staticMenuMobileActive: false,
			menuHoverActive: false,
		}))
		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener()
			this.menuOutsideClickListener = null
		}
		this.unblockBodyScroll()
	}

	blockBodyScroll(): void {
		// SSR guard: hideMenu() runs on NavigationEnd during server render too.
		if (typeof document === 'undefined') return
		if (document.body.classList) {
			document.body.classList.add('blocked-scroll')
		} else {
			document.body.className += ' blocked-scroll'
		}
	}

	unblockBodyScroll(): void {
		// SSR guard: hideMenu() runs on NavigationEnd during server render too.
		if (typeof document === 'undefined') return
		if (document.body.classList) {
			document.body.classList.remove('blocked-scroll')
		} else {
			document.body.className = document.body.className.replace(
				new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
				' '
			)
		}
	}

	get containerClass() {
		return {
			'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
			'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
			'layout-static-inactive':
				this.layoutService.layoutState().staticMenuDesktopInactive &&
				this.layoutService.layoutConfig().menuMode === 'static',
			'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
			'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
		}
	}

	deferredPrompt: any
	showInstallButton = signal(false)
	isIos = signal(
		typeof window !== 'undefined' && typeof window.navigator !== 'undefined' ?
			/iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase())
		:	false
	)
	isInStandaloneMode = signal(
		typeof window !== 'undefined' && typeof window.navigator !== 'undefined' ?
			'standalone' in window.navigator && (window.navigator as any).standalone
		:	false
	)
	shouldShowIosGuide = linkedSignal(() => this.isIos() && !this.isInStandaloneMode())

	@HostListener('window:beforeinstallprompt', ['$event'])
	onbeforeinstallprompt(e: Event) {
		e.preventDefault()
		this.deferredPrompt = e
		this.showInstallButton.set(true)
	}

	addToHomeScreen() {
		if (this.deferredPrompt) {
			this.showInstallButton.set(false)
			this.deferredPrompt.prompt()
			this.deferredPrompt.userChoice.then((choiceResult: any) => {
				this.deferredPrompt = null
			})
		}
	}

	moveToTada() {
		if (window.location.origin.includes('localhost:4200')) {
			window.location.replace('http://localhost:4200/landing')
		} else {
			window.location.replace('https://localhost:4200/login')
		}
	}

	protected readonly LanguageService = LanguageService
}
